import { useState } from "react";
import type { AssetTitle } from "../api/types";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useT } from "../i18n";

const TITLE_LANGUAGES: { code: string; label: string }[] = [
  { code: "en", label: "English (en)" },
  { code: "zh_CN", label: "简体中文 (zh_CN)" },
  { code: "zh_HK", label: "繁體中文 (zh_HK)" },
];

export function TitleManager({ assetId, titles }: { assetId: string; titles: AssetTitle[] }) {
  const { user } = useAuth();
  const { t } = useT();
  const [saving, setSaving] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addLang, setAddLang] = useState("en");
  const [addTitle, setAddTitle] = useState("");
  const [editLang, setEditLang] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!user || !api) return null;

  const canEdit = user.role === "admin" || user.role === "publisher";

  async function handleAdd() {
    if (!addTitle.trim() || adding) return;
    setAdding(true);
    setError(null);
    try {
      await api.addTitle(assetId, addLang, addTitle.trim());
      setAdding(false);
      setAddTitle("");
      window.location.reload();
    } catch (err) {
      setError((err as Error).message);
      setAdding(false);
    }
  }

  async function handleEdit(lang: string) {
    if (!editTitle.trim() || saving) return;
    setSaving(lang);
    setError(null);
    try {
      await api.addTitle(assetId, lang, editTitle.trim());
      setEditLang(null);
      window.location.reload();
    } catch (err) {
      setError((err as Error).message);
      setSaving(null);
    }
  }

  const existingLangs = new Set(titles.map((t) => t.language));
  const availableForAdd = TITLE_LANGUAGES.filter((l) => !existingLangs.has(l.code));

  return (
    <div>
      <h2 className="text-sm font-semibold mb-2">{t("titles.title")}</h2>

      {error && (
        <div className="mb-3 p-2 rounded bg-red-900/30 border border-red-800 text-xs text-red-400">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {titles.map((entry) => (
          <div key={entry.language} className="flex items-center gap-2">
            <span className="w-16 text-xs text-brand-muted shrink-0">{entry.language}</span>
            {editLang === entry.language ? (
              <>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleEdit(entry.language);
                    if (e.key === "Escape") setEditLang(null);
                  }}
                  className="flex-1 px-2 py-1 rounded text-sm bg-brand-dark border border-brand-line focus:border-brand-red outline-none"
                  autoFocus
                />
                <button
                  onClick={() => handleEdit(entry.language)}
                  disabled={saving === entry.language || !editTitle.trim()}
                  className="px-2 py-1 rounded text-xs bg-brand-red text-white disabled:opacity-50"
                >
                  {t("common.save")}
                </button>
                <button
                  onClick={() => { setEditLang(null); setEditTitle(""); }}
                  className="px-2 py-1 rounded text-xs bg-brand-hover text-brand-muted"
                >
                  {t("common.cancel")}
                </button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm">{entry.title}</span>
                {canEdit && (
                  <button
                    onClick={() => { setEditLang(entry.language); setEditTitle(entry.title); }}
                    className="text-xs text-brand-muted hover:text-brand-text"
                  >
                    {t("titles.edit")}
                  </button>
                )}
              </>
            )}
          </div>
        ))}

        {canEdit && availableForAdd.length > 0 && (
          <div className="flex items-center gap-2 pt-2 border-t border-brand-line">
            {adding ? (
              <span className="text-xs text-brand-muted">{t("common.adding")}</span>
            ) : (
              <>
                <select
                  value={addLang}
                  onChange={(e) => setAddLang(e.target.value)}
                  className="px-2 py-1 rounded text-xs bg-brand-dark border border-brand-line text-brand-text outline-none"
                >
                  {availableForAdd.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={addTitle}
                  onChange={(e) => setAddTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAdd();
                  }}
                  placeholder={t("titles.addPlaceholder")}
                  className="flex-1 px-2 py-1 rounded text-sm bg-brand-dark border border-brand-line focus:border-brand-red outline-none"
                />
                <button
                  onClick={handleAdd}
                  disabled={!addTitle.trim()}
                  className="px-3 py-1 rounded text-xs bg-brand-red text-white disabled:opacity-50"
                >
                  {t("titles.add")}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
